<template>
  <div class="my-org">
    <page-hero
        class="mb-1"
        :title="org.name || msg('My Organization')"
        :subtitle="msg('Settings that apply to your whole organization')"
        variant="organization"
        :stats="heroStats"
    >
      <template #illustration>
        <svg viewBox="0 0 100 120" fill="none" xmlns="http://www.w3.org/2000/svg">
          <rect x="22" y="30" width="56" height="48" rx="5" fill="rgba(255,255,255,.2)" stroke="rgba(255,255,255,.25)" stroke-width="1.5"/>
          <rect x="32" y="41" width="26" height="4" rx="2" fill="rgba(255,255,255,.35)"/>
          <rect x="32" y="50" width="16" height="3" rx="1.5" fill="rgba(255,255,255,.25)"/>
          <rect x="32" y="58" width="34" height="3" rx="1.5" fill="rgba(255,255,255,.18)"/>
          <path d="M22 86h56v12H22z" fill="rgba(255,255,255,.15)"/>
          <circle cx="78" cy="28" r="9" fill="rgba(255,255,255,.08)"/>
          <circle cx="14" cy="92" r="11" fill="rgba(255,255,255,.06)"/>
        </svg>
      </template>
    </page-hero>

    <b-alert :show="error.org != null" variant="danger" class="p-1">
      <i class="fas fa-exclamation-circle mr-50"/>{{ error.org }}
    </b-alert>

    <b-card v-if="!loaded.org && error.org == null" class="text-center py-3">
      <b-spinner variant="primary"/>
    </b-card>

    <b-card v-else-if="loaded.org" no-body>
      <b-tabs card>
        <b-tab active>
          <template #title>
            <feather-icon icon="BriefcaseIcon" size="15" class="mr-50"/>{{ msg('General') }}
          </template>
          <org-details v-model="org" @save="saveOrg"/>
        </b-tab>

        <b-tab>
          <template #title>
            <feather-icon icon="CreditCardIcon" size="15" class="mr-50"/>{{ msg('Payments') }}
          </template>
          <org-stripe v-model="org" :can-rotate="isAdminUser" @save="saveOrg"/>
        </b-tab>

        <b-tab>
          <template #title>
            <feather-icon icon="SmartphoneIcon" size="15" class="mr-50"/>{{ msg('Integrations') }}
          </template>
          <org-flexipass v-model="org" @save="saveOrg"/>
        </b-tab>

        <b-tab lazy>
          <template #title>
            <feather-icon icon="GridIcon" size="15" class="mr-50"/>{{ msg('Modules') }}
          </template>
          <org-modules :org-id="org.id"/>
        </b-tab>
      </b-tabs>
    </b-card>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import PageHero from '@/components/PageHero.vue'
import OrgDetails from '@/components/panels/organization/orgDetails'
import OrgModules from '@/components/panels/organization/orgModules'
import OrgStripe from '@/components/panels/organization/orgStripe'
import OrgFlexipass from '@/components/panels/organization/orgFlexipass'

/**
 * Settings for the organization the signed-in user belongs to.
 *
 * OrganizationDetails.vue covers "edit organization #N" and is reached through
 * the organization list; this page answers the far more common "change
 * something about *my* org" without making the user find their own row first.
 * The id comes from the session, never from the route.
 */
export default {
  name: 'MyOrganization',
  components: { PageHero, OrgDetails, OrgModules, OrgStripe, OrgFlexipass },
  data() {
    return {
      org: {
        acronym: null,
        contactName: null,
        country: null,
        crm: null,
        cssLink: null,
        email: null,
        hebCalendar: false,
        id: 0,
        lang: null,
        logoLink: null,
        logoMinLink: null,
        modules: [],
        name: null,
        phone: null,
        price: 0,
        priceType: null,
        settings: {},
        // declared up front so Vue 2 tracks them before the fetch resolves
        stripeApiKey: null,
        stripePublicApiKey: null,
        stripeRestrictedApiKey: null,
        flexipassUsername: null,
        flexipassPassword: null,
        vat: 0,
        website: null
      },
      loaded: { org: false },
      error: { org: null }
    }
  },
  created() {
    this.refreshOrg()
  },
  computed: {
    ...mapGetters('data', ['getOrganization']),
    ...mapGetters('user', ['myAdminStatus', 'myRoleId']),
    /** Same check buildingManagement uses — profile.isAdmin is not always populated. */
    isAdminUser() {
      return this.myRoleId === 1 || this.myAdminStatus === true
    },
    paymentsReady() {
      const secret = this.org.stripeApiKey
      const publishable = this.org.stripePublicApiKey
      return [secret, publishable].every(k => k != null && String(k).trim() !== '')
    },
    heroStats() {
      if (!this.loaded.org) return []
      const stats = []
      if (this.org.country) stats.push({ icon: 'MapPinIcon', label: this.org.country })
      if (this.org.lang) stats.push({ icon: 'GlobeIcon', label: String(this.org.lang).toUpperCase() })
      stats.push({
        icon: 'CreditCardIcon',
        label: this.paymentsReady ? this.msg('Payments configured') : this.msg('Payments not configured'),
        variant: this.paymentsReady ? 'success' : 'warning'
      })
      return stats
    }
  },
  methods: {
    ...mapActions('organizations', ['get', 'update']),
    /**
     * The org already in the store comes from getUserOrganization(), which
     * returns the basic copy — no Stripe keys. Re-fetch the full record so the
     * Payments tab has something to edit.
     */
    refreshOrg() {
      const id = this.getOrganization != null ? this.getOrganization.id : null
      if (id == null || id === 0) {
        this.error.org = this.msg('Your user is not linked to an organization')
        return
      }
      this.get(id).then(
          org => {
            this.org = { ...this.org, ...org }
            this.loaded.org = true
          },
          e => { this.error.org = e }
      )
    },
    saveOrg() {
      // the store already raises the error toast — swallow so the rejection
      // does not surface as an unhandled promise
      this.update(this.org).then(() => this.refreshOrg(), () => {})
    }
  }
}
</script>

<style scoped>
</style>
